import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldHalved,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import adminSecurityService from '../../../helpers/adminSecurityService';

const BlockIPForm = () => {
    const [formData, setFormData] = useState({
        ip: '',
        reason: '',
        durationHours: 24,
        isPermanent: false,
    });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    const durationOptions = [
        { value: 1, label: '1 hora' },
        { value: 6, label: '6 horas' },
        { value: 12, label: '12 horas' },
        { value: 24, label: '24 horas' },
        { value: 48, label: '48 horas' },
        { value: 72, label: '72 horas' },
        { value: 168, label: '7 días' },
        { value: 720, label: '30 días' },
        { value: 8760, label: '1 año' },
    ];

    const predefinedReasons = [
        'Spam masivo detectado',
        'Ataque continuo',
        'Patrones sospechosos',
        'Bloqueo preventivo',
        'Comportamiento anómalo',
        'Múltiples intentos fallidos',
    ];

    const validateIP = (ip) => {
        // Validación básica de IPv4
        const ipv4Regex =
            /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        // Validación básica de IPv6 (simplificada)
        const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
        return ipv4Regex.test(ip) || ipv6Regex.test(ip);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setResult(null);
        setValidationErrors({});

        // Validaciones
        const errors = {};
        if (!formData.ip.trim()) {
            errors.ip = 'La IP es requerida';
        } else if (!validateIP(formData.ip)) {
            errors.ip = 'Formato de IP inválido';
        }
        if (!formData.reason.trim()) {
            errors.reason = 'La razón del bloqueo es requerida';
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Confirmación
        const confirmMessage = formData.isPermanent
            ? `¿Estás seguro de que deseas bloquear permanentemente la IP ${formData.ip}? Esta acción no se puede deshacer fácilmente.`
            : `¿Estás seguro de que deseas bloquear la IP ${formData.ip} por ${formData.durationHours} horas?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setLoading(true);
        try {
            const response = await adminSecurityService.blockIP(
                formData.ip,
                formData.reason,
                formData.isPermanent ? undefined : formData.durationHours,
                formData.isPermanent
            );
            setResult(response);
            // Reset form
            setFormData({
                ip: '',
                reason: '',
                durationHours: 24,
                isPermanent: false,
            });
        } catch (err) {
            console.error('Error blocking IP:', err);
            setError(err.message || 'Error al bloquear la IP');
        } finally {
            setLoading(false);
        }
    };

    const handlePredefinedReason = (reason) => {
        setFormData({ ...formData, reason });
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            {/* Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Información sobre bloqueo de IPs</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Los bloqueos temporales expiran automáticamente</li>
                            <li>Los bloqueos permanentes requieren desbloqueo manual</li>
                            <li>Las IPs bloqueadas no podrán acceder a los formularios embebibles</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faShieldHalved} className="text-red-600 text-xl" />
                        <h3 className="text-xl font-semibold text-gray-900">Bloquear IP Manualmente</h3>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* IP Address */}
                    <div>
                        <label htmlFor="ip" className="block text-sm font-medium text-gray-700 mb-2">
                            Dirección IP <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="ip"
                            value={formData.ip}
                            onChange={(e) => setFormData({ ...formData, ip: e.target.value })}
                            placeholder="Ej: 82.165.87.222"
                            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${validationErrors.ip ? 'border-red-300' : 'border-gray-300'
                                }`}
                        />
                        {validationErrors.ip && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.ip}</p>
                        )}
                    </div>

                    {/* Reason */}
                    <div>
                        <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                            Razón del Bloqueo <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="reason"
                            value={formData.reason}
                            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                            placeholder="Describe la razón del bloqueo..."
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${validationErrors.reason ? 'border-red-300' : 'border-gray-300'
                                }`}
                        />
                        {validationErrors.reason && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.reason}</p>
                        )}

                        {/* Predefined Reasons */}
                        <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-2">Razones predefinidas:</p>
                            <div className="flex flex-wrap gap-2">
                                {predefinedReasons.map((reason) => (
                                    <button
                                        key={reason}
                                        type="button"
                                        onClick={() => handlePredefinedReason(reason)}
                                        className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                    >
                                        {reason}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Block Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Tipo de Bloqueo
                        </label>
                        <div className="space-y-3">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    checked={!formData.isPermanent}
                                    onChange={() => setFormData({ ...formData, isPermanent: false })}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-700">Temporal</span>
                            </label>
                            {!formData.isPermanent && (
                                <div className="ml-6">
                                    <select
                                        value={formData.durationHours}
                                        onChange={(e) =>
                                            setFormData({ ...formData, durationHours: parseInt(e.target.value) })
                                        }
                                        className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    >
                                        {durationOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    checked={formData.isPermanent}
                                    onChange={() => setFormData({ ...formData, isPermanent: true })}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-700">Permanente</span>
                            </label>
                            {formData.isPermanent && (
                                <div className="ml-6 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                    <div className="flex items-start gap-2">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mt-0.5" />
                                        <div className="text-sm text-yellow-800">
                                            <strong>Advertencia:</strong> Los bloqueos permanentes requieren desbloqueo
                                            manual. Úsalos solo para casos graves.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`
                flex items-center gap-2 px-6 py-2 rounded-md font-medium transition-colors
                ${loading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-red-600 text-white hover:bg-red-700'
                                }
              `}
                        >
                            {loading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    Bloqueando...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faShieldHalved} />
                                    Bloquear IP
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Result */}
                {result && (
                    <div className="mx-6 mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5" />
                            <div className="flex-1">
                                <h5 className="font-semibold text-green-900 mb-1">IP bloqueada exitosamente</h5>
                                <div className="text-sm text-green-800 space-y-1">
                                    <p>
                                        <strong>IP:</strong> {result.ip}
                                    </p>
                                    <p>
                                        <strong>Razón:</strong> {result.reason}
                                    </p>
                                    <p>
                                        <strong>Tipo:</strong>{' '}
                                        {result.isPermanent ? 'Permanente' : 'Temporal'}
                                    </p>
                                    {!result.isPermanent && result.blockedUntil && (
                                        <p>
                                            <strong>Bloqueado hasta:</strong>{' '}
                                            {new Date(result.blockedUntil).toLocaleString('es-ES')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="mx-6 mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
                            <div className="flex-1">
                                <h5 className="font-semibold text-red-900 mb-1">Error al bloquear IP</h5>
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlockIPForm;

